import React, { FC } from "react";
import { useValidateOnLanguageChange } from "../../../hooks/useValidateOnLanguageChange";
import { Alert } from "@saif/alert";
import { Button } from "@saif/button";
import { Form, Formik, FormikHelpers } from "formik";
import { FormTextInput } from "@saif/form-text-input";
import { FormEmailInput } from "@saif/form-email-input";
import { FormPolicyNumberInput } from "@saif/form-policy-number-input";
import { FormFeinInput } from "@saif/form-fein-input";
import PermissionCheckboxGroup from "../../FormFields/Permissions/PolicyHolderPermissions/PolicyPermissionsGroup";
import "./PolicyHolderRFA.scss";

interface UserData {
  FirstName: string;
  LastName: string;
  EmailAddress: string;
  PolicyNumber: string;
  FEIN: string;
  Language: string;
  permissions: string[];
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  policy: string;
  fein: string;
  permissions: string[];
}

interface PolicyHolderRFAProps {
  getValue: (section: string, key: string) => string;
  locale: string;
  dataFail: boolean;
  onSubmit: (userData: UserData) => Promise<void> | void;
}

const PolicyHolderRFA: FC<PolicyHolderRFAProps> = (props) => {
  const { getValue, locale, dataFail, onSubmit } = props;

  const formikRef = useValidateOnLanguageChange(locale);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    const userData: UserData = {
      FirstName: values.firstName.trim(),
      LastName: values.lastName.trim(),
      EmailAddress: values.email.trim(),
      PolicyNumber: values.policy.trim(),
      FEIN: values.fein.slice(0, 2) + "-" + values.fein.slice(2),
      Language: locale,
      permissions: values.permissions,
    };

    try {
      await onSubmit(userData);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        firstName: "",
        lastName: "",
        email: "",
        policy: "",
        fein: "",
        permissions: [] as string[],
      }}
      validateOnChange={false}
      validateOnBlur={true}
      onSubmit={handleSubmit}
    >
      {({ status, isSubmitting }) => (
        <Form className="policy-info-form" noValidate>
          <FormTextInput
            name="firstName"
            label={getValue("RequestAccess", "FirstNameLabel")}
            maxLength={50}
          />
          <FormTextInput
            name="lastName"
            label={getValue("RequestAccess", "LastNameLabel")}
            maxLength={50}
          />
          <FormEmailInput
            name="email"
            label={getValue("RequestAccess", "EmailLabel")}
            hintText={
              getValue("RequestAccess", "EmailDescPrefix") +
              getValue("RequestAccess", "EmailDescMiddle") +
              getValue("RequestAccess", "EmailDescSuffix")
            }
          />
          <FormPolicyNumberInput
            label={getValue("RequestAccess", "PolicyNumberLabel")}
            name="policy"
            hintText={getValue("RequestAccess", "PolicySub")}
          />
          {/*@ts-expect-error: label is required in types but has a default in implementation*/}
          <FormFeinInput />
          <PermissionCheckboxGroup getValue={getValue} />
          <Alert color="danger" dismissible={false} show={dataFail}>
            {getValue("RequestAccess", "ValRFA")}
          </Alert>
          <Button type="submit" color="primary" isLoading={isSubmitting}>
            {getValue("RequestAccess", "Submit")}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default PolicyHolderRFA;
